import React from 'react';
import { CheckCircle, Crown } from 'lucide-react';
import { Modal } from 'antd';

interface ResellerLevel {
	level: string;
	min: number;
	max: number;
	features: string[];
}

interface ResellerLevelsModalProps {
	showLevel: boolean;
	closeLevelModal: () => void;
	resellerLevels: ResellerLevel[];
	currentLevel: string;
}

const levelColors: Record<string, string> = {
	Lite: 'bg-gray-400',
	Bronze: 'bg-amber-600',
	Silver: 'bg-gray-500',
	Gold: 'bg-yellow-500',
	Platinum: 'bg-blue-700',
};

const ResellerLevelsModal: React.FC<ResellerLevelsModalProps> = ({
	showLevel,
	closeLevelModal,
	resellerLevels,
	currentLevel,
}) => {
	const currentLevelIndex = resellerLevels.findIndex((l) => l.level === currentLevel);

	return (
		<Modal
			open={showLevel}
			onCancel={closeLevelModal}
			footer={null}
			width={600}
			className="reseller-modal"
		>
			<div className="p-3">
				<h2 className="text-2xl font-bold mb-6 text-gray-800">Your Partnership Journey</h2>

				<div className="relative mb-4">
					{/* Background track (gray) */}
					<div className="absolute h-1 bg-gray-200 top-7 left-5 right-5 z-0" />

					{/* Colored progress track */}
					{currentLevelIndex > 0 && (
						<div
							className="absolute h-1 bg-green-500 top-7 z-0"
							style={{
								left: '5%',
								width: `${(currentLevelIndex / (resellerLevels.length - 1)) * 90}%`,
							}}
						/>
					)}

					<div className="flex justify-between relative z-10">
						{resellerLevels.map((data, index) => {
							const isCurrent = currentLevelIndex === index;
							const isCompleted = currentLevelIndex > index;
							const colorClass = isCompleted
								? 'bg-green-500'
								: levelColors[data.level] || 'bg-gray-400';

							return (
								<div key={index} className="flex flex-col items-center">
									<div
										className={`w-14 h-14 rounded-full flex items-center justify-center ${colorClass} text-white`}
									>
										{isCompleted ? (
											<CheckCircle size={28} />
										) : isCurrent ? (
											<Crown size={28} className="text-[#ffffff]" />
										) : null}
									</div>
									<p className={'text-sm mt-2 font-medium text-gray-800'}>{data.level}</p>
								</div>
							);
						})}
					</div>
				</div>

				<div className="space-y-4">
					{resellerLevels.map((data, index) => {
						const isCurrent = data.level === currentLevel;
						const isCompleted = currentLevelIndex > index;
						const colorClass = isCompleted
							? 'bg-green-500'
							: levelColors[data.level] || 'bg-gray-400';

						return (
							<div
								key={data.level}
								className={`p-3 rounded-xl transition-all ${
									isCurrent
										? 'bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 shadow-sm'
										: isCompleted
											? 'bg-green-50 border border-green-100'
											: 'bg-gray-50 border border-gray-200'
								}`}
							>
								<div className="flex items-center">
									<div
										className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 ${colorClass} text-white`}
									>
										{isCompleted ? (
											<CheckCircle size={20} />
										) : isCurrent ? (
											<Crown size={20} className="text-[#ffffff]" />
										) : null}
									</div>

									<div className="flex-1">
										<div className="flex justify-between items-center">
											<h3 className="text-lg font-bold text-gray-800 m-0">{data.level}</h3>
											<div className="flex items-center">
												<span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-200 text-gray-700">
													{data.max === Infinity
														? `${data.min}+ seats`
														: `${data.min}-${data.max} seats`}
												</span>
												{isCurrent && (
													<span className="ml-3 px-3 py-1 rounded-full text-sm font-medium bg-blue-600 text-white">
														Current
													</span>
												)}
											</div>
										</div>

										<div className="mt-2 flex flex-wrap gap-y-2">
											{data.features.map((feature, idx) => (
												<div key={idx} className="flex items-center w-full text-gray-600">
													<div className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center mr-2">
														<CheckCircle size={12} className="text-gray-600" />
													</div>
													<span className="text-sm">{feature}</span>
												</div>
											))}
										</div>
									</div>
								</div>
							</div>
						);
					})}
				</div>
			</div>
		</Modal>
	);
};

export default ResellerLevelsModal;
